import { useMemo } from "react";
import {
  AccountSelectGroup,
  AccountsApiListAndSearchAllAccountsRequest,
  PublicAccount,
  useSelectGroupsId,
  Account,
} from "@rentablo/api/apiv1";
import { usePublicOrPrivateAccounts } from "@features/sharedPortfolio/hooks/usePublicOrPrivateAccounts.ts";

export const filterOutHiddenAccounts = (accounts?: PublicAccount[]) => {
  const porfolioStatesMap =
    accounts?.reduce(
      (acc, account) => {
        if (account.contraAccountId) {
          acc[account.contraAccountId] = account.isHidden ?? false;
        }
        return acc;
      },
      {} as Record<number, boolean>
    ) ?? {};
  return (
    accounts?.filter(
      (account) =>
        !(
          account.isHidden ||
          (!account.contraAccountId && porfolioStatesMap[account.id])
        )
    ) ?? []
  );
};

const prepareGroups = (
  id: string,
  accounts?: (Account | PublicAccount)[]
): AccountSelectGroup => ({
  name: id,
  accountIds: accounts?.map((account) => account.id) ?? [],
});

export const useSelectGroupsForId = ({
  id,
  accountParams,
  refetchOnWindowFocus = true,
}: {
  id: string;
  accountParams?: AccountsApiListAndSearchAllAccountsRequest;
  refetchOnWindowFocus?: boolean;
}) => {
  const {
    accounts,
    isFetching: isFetchingAccounts,
    isPending: isPendingAccounts,
  } = usePublicOrPrivateAccounts(accountParams);

  const {
    data: selectedGroups,
    refetch,
    isError,
    isPending: isPendingGroups,
    isFetching: isFetchingGroups,
  } = useSelectGroupsId(
    {
      id,
    },
    {
      refetchOnWindowFocus,
    }
  );

  const visibleAccounts = useMemo(
    () => filterOutHiddenAccounts(accounts),
    [accounts]
  );
  const visibleAccountIds = useMemo(
    () => visibleAccounts?.map((account) => account.id) ?? [],
    [visibleAccounts]
  );

  const filteredGroupData = useMemo(
    () => ({
      ...selectedGroups,
      accountIds:
        selectedGroups?.accountIds?.filter((id) =>
          visibleAccountIds.includes(id)
        ) ?? [],
    }),
    [selectedGroups, visibleAccountIds]
  );

  const isFetching = isFetchingAccounts || isFetchingGroups;
  const isPending = isPendingAccounts || isPendingGroups;

  const data = useMemo(() => {
    return isPending
      ? undefined
      : isError
        ? prepareGroups(id, visibleAccounts)
        : filteredGroupData.accountIds?.length > 0
          ? filteredGroupData
          : prepareGroups(id, visibleAccounts);
  }, [visibleAccounts, filteredGroupData, id, isError, isPending]);

  const selectedAccounts = useMemo(
    () =>
      visibleAccounts?.filter((account) =>
        data?.accountIds?.includes(account.id)
      ),
    [data, visibleAccounts]
  );

  return {
    data,
    accounts: visibleAccounts,
    selectedAccounts,
    refetch,
    isError,
    isFetching,
    isPending,
  };
};
